import React from 'react';
import { Link } from 'react-router-dom';

const allPlanFeatures = [
  'Unlimited email campaigns',
  'Contact management & segmentation',
  'Advanced analytics & reporting',
  'Campaign scheduling & automation',
  'Branded email builder',
  'Bounce & complaint tracking',
  'API access',
  'Priority support',
];

const plans = [
  {
    name: 'Starter',
    price: '$99',
    period: 'per month',
    desc: 'For solo advisors and small agencies getting their first list off the ground.',
    features: ['Up to 5,000 contacts', '10,000 emails / month', 'Basic analytics', 'Email support'],
    cta: 'Register',
    href: '/signup',
    popular: false,
  },
  {
    name: 'Professional',
    price: '$299',
    period: 'per month',
    desc: 'For growing agencies sending weekly across several brands.',
    features: ['Up to 25,000 contacts', '100,000 emails / month', 'Advanced analytics', 'Campaign automation', 'Priority support'],
    cta: 'Register',
    href: '/signup',
    popular: true,
  },
  {
    name: 'Enterprise',
    price: 'Custom',
    period: 'pricing',
    desc: 'For agency networks and large operators with their own deliverability requirements.',
    features: ['Unlimited contacts', 'Unlimited emails', 'Custom analytics', 'Dedicated account manager', '24/7 support', 'Custom integrations'],
    cta: 'Contact us',
    href: '/help',
    popular: false,
  },
];

const Pricing: React.FC = () => (
  <>
    <section className="text-center px-4 sm:px-6 py-16 sm:py-24 max-w-5xl mx-auto">
      <h1
        className="text-5xl sm:text-6xl md:text-7xl font-bold text-[var(--text)] leading-[1.05] tracking-tight mb-6"
        style={{ fontFamily: "'Quicksand', sans-serif" }}
      >
        Pricing that scales<br />with your list.
      </h1>
      <p className="text-lg sm:text-xl md:text-2xl text-[var(--text-muted)] leading-relaxed max-w-2xl mx-auto">
        Every plan includes the core marketing surface. Scale up when your list,
        send volume, or service needs cross the thresholds.
      </p>
    </section>

    <section className="px-4 sm:px-6 pb-12 sm:pb-16">
      <div className="max-w-6xl mx-auto grid gap-6 md:grid-cols-3">
        {plans.map((plan) => (
          <div
            key={plan.name}
            className={`relative bg-white rounded-xl p-7 flex flex-col border ${
              plan.popular ? 'border-[var(--accent)] shadow-md' : 'border-[var(--border)]'
            }`}
          >
            {plan.popular && (
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[var(--primary)] text-white text-xs font-semibold px-3 py-1 rounded-full shadow-sm">
                Most popular
              </span>
            )}
            <h3 className="text-lg font-semibold text-[var(--text)] mb-1">{plan.name}</h3>
            <p className="text-sm text-[var(--text-muted)] mb-5 leading-relaxed">{plan.desc}</p>
            <div className="mb-5">
              <span className="text-4xl font-bold text-[var(--primary)]">{plan.price}</span>
              <span className="text-sm text-[var(--text-muted)] ml-2">{plan.period}</span>
            </div>
            <ul className="space-y-2 text-sm mb-7">
              {plan.features.map((f) => (
                <li key={f} className="flex gap-2 text-[var(--text)]">
                  <span className="text-[var(--accent)]">✓</span>
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <Link
              to={plan.href}
              className={`block text-center mt-auto rounded-lg py-3 text-sm font-semibold transition-colors ${
                plan.popular
                  ? 'bg-[var(--primary)] text-white hover:bg-[var(--primary-light)] shadow-sm'
                  : 'bg-white border border-[var(--border)] text-[var(--text)] hover:border-[var(--primary)] hover:text-[var(--primary)]'
              }`}
            >
              {plan.cta}
            </Link>
          </div>
        ))}
      </div>
    </section>

    <section className="px-4 sm:px-6 py-12 sm:py-16 bg-[var(--bg)]">
      <div className="max-w-5xl mx-auto">
        <h2
          className="text-2xl sm:text-3xl font-bold text-[var(--text)] text-center mb-8"
          style={{ fontFamily: "'Quicksand', sans-serif" }}
        >
          Every plan includes
        </h2>
        <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 md:grid-cols-4">
          {allPlanFeatures.map((f) => (
            <div key={f} className="flex items-center gap-2 bg-white border border-[var(--border)] rounded-lg px-4 py-3">
              <span className="text-[var(--accent)]">✓</span>
              <span className="text-sm text-[var(--text)]">{f}</span>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="px-4 sm:px-6 py-16 sm:py-24 text-center">
      <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[var(--text)] mb-5 leading-tight" style={{ fontFamily: "'Quicksand', sans-serif" }}>
        Questions about which plan fits?
      </h2>
      <p className="text-lg text-[var(--text-muted)] mb-10 max-w-xl mx-auto">
        Tell us about your list size, sending cadence, and brand setup, and
        we'll point you to the right plan — or build a custom one.
      </p>
      <Link
        to="/help"
        className="bg-[var(--primary)] text-white px-8 py-3.5 rounded-lg font-semibold text-base hover:bg-[var(--primary-light)] transition-colors inline-block shadow-sm"
      >
        Contact us
      </Link>
    </section>
  </>
);

export default Pricing;
